import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Paper,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  Description as DocumentIcon,
  AttachFile as FileIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface AttachmentPopupProps {
  open: boolean;
  onClose: () => void;
  onSelectImage: () => void;
  onSelectVideo: () => void;
  onSelectDocument: () => void;
  onSelectFile: () => void;
}

const AttachmentPopup: React.FC<AttachmentPopupProps> = ({
  open,
  onClose,
  onSelectImage,
  onSelectVideo,
  onSelectDocument,
  onSelectFile,
}) => {
  const theme = useTheme();

  const attachmentOptions = [
    {
      id: 'image',
      label: 'Image',
      icon: ImageIcon,
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      onClick: onSelectImage,
    },
    {
      id: 'video',
      label: 'Video',
      icon: VideoIcon,
      color: '#F44336',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      onClick: onSelectVideo,
    },
    {
      id: 'document',
      label: 'Document',
      icon: DocumentIcon,
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      onClick: onSelectDocument,
    },
    {
      id: 'file',
      label: 'Other File',
      icon: FileIcon,
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      onClick: onSelectFile,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ position: 'relative', p: 3 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'text.secondary',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Attach File
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr' },
            gap: 2,
          }}
        >
          {attachmentOptions.map((option, index) => {
            const IconComponent = option.icon;
            return (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  onClick={() => {
                    option.onClick();
                    onClose();
                  }}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    border: `2px solid ${alpha(option.color, 0.2)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 24px ${alpha(option.color, 0.3)}`,
                      borderColor: option.color,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: option.gradient,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover::before': {
                      opacity: 0.1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: option.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 8px 16px ${alpha(option.color, 0.3)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1) rotate(5deg)',
                        },
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 32,
                          color: 'white',
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        color: 'text.primary',
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
};

export default AttachmentPopup;

